/**
 * Évaluation sûre d'une petite formule arithmétique (primitive `Formula`).
 *
 * Pas d'`eval` : un analyseur à descente récursive qui ne connaît que les nombres,
 * les variables fournies, `+ - * /`, les parenthèses et les fonctions
 * `ceil floor round min max abs`. Toute autre chose lève une erreur.
 * Fichier séparé du composant : un module qui exporte autre chose qu'un composant
 * casserait le Fast Refresh de Vite.
 */

const FUNCTIONS = {
  ceil: (a) => Math.ceil(a),
  floor: (a) => Math.floor(a),
  round: (a) => Math.round(a),
  abs: (a) => Math.abs(a),
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
}

const tokenize = (src) => {
  const tokens = []
  const re = /\s*(?:(\d+(?:\.\d+)?|\.\d+)|([A-Za-z_][A-Za-z0-9_]*)|([-+*/(),]))/y
  let pos = 0
  while (pos < src.length) {
    re.lastIndex = pos
    const m = re.exec(src)
    if (!m) {
      if (/^\s*$/.test(src.slice(pos))) break
      throw new Error(`Formula: unexpected character at ${pos}`)
    }
    if (m[1] !== undefined) tokens.push({ type: 'num', value: Number(m[1]) })
    else if (m[2] !== undefined) tokens.push({ type: 'id', value: m[2] })
    else tokens.push({ type: 'op', value: m[3] })
    pos = re.lastIndex
  }
  return tokens
}

/** Évalue `expression` avec les variables `vars` ; lève une erreur si invalide. */
export const evaluateFormula = (expression, vars = {}) => {
  const tokens = tokenize(String(expression ?? ''))
  let i = 0
  const peek = () => tokens[i]
  const take = (value) => {
    const t = tokens[i]
    if (!t || (value !== undefined && t.value !== value)) throw new Error(`Formula: expected ${value}`)
    i++
    return t
  }
  const expr = () => {
    let left = term()
    while (peek()?.type === 'op' && (peek().value === '+' || peek().value === '-')) {
      const op = take().value
      const right = term()
      left = op === '+' ? left + right : left - right
    }
    return left
  }
  const term = () => {
    let left = unary()
    while (peek()?.type === 'op' && (peek().value === '*' || peek().value === '/')) {
      const op = take().value
      const right = unary()
      left = op === '*' ? left * right : left / right
    }
    return left
  }
  const unary = () => {
    if (peek()?.type === 'op' && (peek().value === '-' || peek().value === '+')) {
      const op = take().value
      const v = unary()
      return op === '-' ? -v : v
    }
    return primary()
  }
  const primary = () => {
    const t = take()
    if (t.type === 'num') return t.value
    if (t.type === 'op' && t.value === '(') {
      const v = expr()
      take(')')
      return v
    }
    if (t.type === 'id') {
      if (peek()?.type === 'op' && peek().value === '(') {
        const fn = FUNCTIONS[t.value]
        if (!fn) throw new Error(`Formula: unknown function ${t.value}`)
        take('(')
        const args = [expr()]
        while (peek()?.value === ',') {
          take(',')
          args.push(expr())
        }
        take(')')
        return fn(...args)
      }
      if (!Object.prototype.hasOwnProperty.call(vars, t.value)) throw new Error(`Formula: unknown variable ${t.value}`)
      const raw = vars[t.value]
      const n = raw === '' || raw == null ? NaN : Number(raw)
      return n
    }
    throw new Error('Formula: unexpected token')
  }
  const value = expr()
  if (i < tokens.length) throw new Error('Formula: trailing tokens')
  return value
}
